import { enqueueSnackbar } from 'notistack';
import { useState, useEffect, useCallback } from 'react';

import { Box, Stack } from "@mui/system";
import { Button, TextField } from "@mui/material";
import Typography from '@mui/material/Typography';

import CommentCard from './comment-card';
import { useBoolean } from '../../hooks/use-boolean';
import { LoadingScreen } from '../../components/loading-screen';
import { useDjangoApi } from '../djangoServerUtilities/djangoHandler';

type Props = {
  parentId: string;
  parentType: string;
  noteToUsers?: string;
}

/*
 This element should be used anywhere that we want to display comments on a form. Examples: referral requests, matching, consultation notes(?), etc.
 */
export default function FormCommentsSection({ parentId, parentType, noteToUsers, }: Props) {
  const djangoApi = useDjangoApi();
  const [comments, setComments] = useState<{ id: string, note: string, created_at: string, created_by: string, updated_at: string, updated_by: string }[]>([]);
  const [inputComment, setInputComment] = useState<string>('');
  // const [height, setHeight] = useState<number>(400);
  const isPosting = useBoolean(false);
  const isLoading = useBoolean(false);

  const fetchComments = useCallback(async () => {
    isLoading.onTrue()
    const response = await djangoApi.get(`/comments/?type=${parentType}&id=${parentId}&is_deleted=false&ordering=-created_at`);
    if (response && response.status === 200) {
      setComments(response.data.results);
    }
    isLoading.onFalse()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [djangoApi, parentId, parentType])

  const postComment = async () => {
    if (inputComment.length === 0) return;
    isPosting.onTrue()
    const response = await djangoApi.post(`/comments/`, {
      "note": inputComment,
      "related_entity": {
        "type": parentType,
        "id": parentId,
      }
    }
    )
    if (response && response.status === 201)
      setInputComment('');
    else
      enqueueSnackbar("Unable to add comment", { variant: 'error' });

    fetchComments();
    isPosting.onFalse()
  }

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (

    <Stack
      direction='row'
      sx={{
        width: '500px',
        maxHeight: '100%',
      }}>
      <Box sx={{
        mx: 2,
        borderLeft: '1px solid #e0e0e0',
      }} />


      <Stack sx={{
        width: '100%',
        /**
         * This dynamically sets the height of the comments section based on the height of the associated content (referral request form, transfer request form, etc.) 
         * minus the height of the add a comment box. Defaults to 400px. This allows the comments section to be as tall as possible while not stretching the associated content. 
         */
        height: '500px',
      }}>
        {/* Associated Height: {height}px */}
        <Stack spacing={2} sx={{ pb: 1 }}>
          {noteToUsers && <Typography variant="body2">{noteToUsers}</Typography>}
          <TextField
            id="outlined-multiline-flexible"
            placeholder="Add a comment"
            multiline
            value={inputComment}
            disabled={isPosting.value}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                postComment();
              }
            }}
            maxRows={4}
            minRows={4}
            sx={{
              backgroundColor: 'white',
              width: '100%',
            }}
            onChange={(e) => {
              setInputComment(e.target.value);
            }}
          />
          <Button variant="contained" color="primary" disabled={isPosting.value} onClick={postComment}>Submit Comment</Button>
        </Stack>
        {isLoading.value ?
          <Stack sx={{ height: '100%' }}>
            <LoadingScreen />
          </Stack>
          :
          <Stack
            direction='column'
            spacing={1.5}
            sx={{
              overflowY: 'scroll',
              // scrollbarWidth: 'none',
              py: 1.5,
            }}>
            {comments.length > 0 ?
              comments.map((comment, index) => (
                <CommentCard key={index} comment={comment} reloadComments={fetchComments} />
              ))
              :
              <Typography textAlign='center' sx={{ width: '100%' }}>
                No comments yet
              </Typography>
            }
          </Stack>
        }
      </Stack>
    </Stack>

  );
}
